// この端末からリレーへ送った転送の控え。
// ページを閉じる/リロードすると deleteToken を失って取り消せなくなる問題への対策として
// localStorage に残す。表示時はリレーに存在確認し、まだ残っているものだけを出す。

const LS_KEY = "facemaker.sendHistory";
const MAX_ENTRIES = 50;

export interface SendHistoryEntry {
  code: string;
  deleteToken: string;
  expiresAt: string; // ISO文字列
  fileName: string;
  kind: "fmavatar" | "clippack";
  createdAt: string; // ISO文字列
}

function load(): SendHistoryEntry[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is SendHistoryEntry =>
        e &&
        typeof e.code === "string" &&
        typeof e.deleteToken === "string" &&
        typeof e.expiresAt === "string" &&
        typeof e.fileName === "string" &&
        (e.kind === "fmavatar" || e.kind === "clippack") &&
        typeof e.createdAt === "string",
    );
  } catch {
    return [];
  }
}

function save(entries: SendHistoryEntry[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(entries));
  } catch {
    /* プライベートブラウジング等では localStorage が使えない */
  }
}

/** 送信履歴に1件追記する。件数が多くなりすぎたら古い順に削る。 */
export function addSendHistoryEntry(entry: SendHistoryEntry): void {
  try {
    const entries = load();
    entries.push(entry);
    entries.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    while (entries.length > MAX_ENTRIES) entries.shift();
    save(entries);
  } catch {
    /* 保存できなくても今回のセッションでは使えないだけ */
  }
}

/**
 * 送信履歴を一覧取得する（新しい順）。
 * 有効期限が過ぎたエントリはリレー側で自然消滅しているため、取得時に取り除いて保存し直す。
 */
export function listSendHistoryEntries(): SendHistoryEntry[] {
  try {
    const entries = load();
    const now = Date.now();
    const alive = entries.filter((e) => {
      const t = Date.parse(e.expiresAt);
      return Number.isNaN(t) || t > now;
    });
    if (alive.length !== entries.length) save(alive);
    return alive.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch {
    return [];
  }
}

/**
 * リレーへの存在確認結果を反映する。alive=false（受信済み・取り消し済み・期限切れ）は取り除き、
 * alive のものはサーバーの有効期限で更新する。結果に含まれない code はそのまま残す。
 */
export function applyUploadStatus(statuses: Array<{ code: string; alive: boolean; expiresAt?: string }>): void {
  try {
    const byCode = new Map(statuses.map((s) => [s.code, s]));
    const next: SendHistoryEntry[] = [];
    for (const e of load()) {
      const s = byCode.get(e.code);
      if (!s) next.push(e);
      else if (s.alive) next.push(s.expiresAt ? { ...e, expiresAt: s.expiresAt } : e);
    }
    save(next);
  } catch {
    /* 保存できなくても表示に影響するだけ */
  }
}

/** 指定 code のエントリを削除する。 */
export function removeSendHistoryEntry(code: string): void {
  try {
    const entries = load().filter((e) => e.code !== code);
    save(entries);
  } catch {
    /* 保存できなくても実害はない */
  }
}
